import React, { useEffect, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { getAdminFromLocalS, history } from '../../utils';
import { saveLoginRedirect } from '../../util/loginRedirect';
import { logOut } from '../../api/admin';
import { connect } from 'react-redux';
import { adminLoaded, newLogin } from '../../actionCreators/admin';
import { getAllSystems } from '../../actionCreators/system';
import Login from './login';
import Success from './resetPasswordSent';
import CustomToast from '../../components/Redesing/custom-toast';
import ResetPassword from './resetPassword';
import SetPasswordRedux from './setPassword';
import { IAdmin } from '../../ts/interface/admins.interface';
import withTheme from './assets/withTheme';
import { ILoginPageProps } from '../../ts/interface/pages/login.interface';
import useQueryString from '../../hooks/queryParams/useQueryString';
import { getOrganization } from '../../api/organization';
import { IAdminResponse, useAdminStore } from '../../store/adminStore';

// Wrap at module scope: calling withTheme() during render creates a new
// component type each time, remounting the subtree on every render
const ResetPasswordWithTheme = withTheme(ResetPassword);
const SetPasswordReduxWithTheme = withTheme(SetPasswordRedux);
const SuccessWithTheme = withTheme(Success);
const LoginWithTheme = withTheme(Login);

const LoginPage: React.FC<ILoginPageProps> = ({
  getAllSystems,
  adminLoaded,
  newLogin,
  dispatch,
  admin,
  location,
}) => {
  const {
    email: emailParam,
    page,
    token,
  } = useParams<{
    email: string;
    token: string;
    page: string;
  }>();
  const systemId = useQueryString().queryParams.get('systemId');
  const organizationId = useQueryString().queryParams.get('organizationId');
  const [password, setPassword] = useState('');
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [resetPasswordState, setResetPasswordState] = useState(false);
  const [adminState, setAdminState] = useState<IAdmin | null>(admin);
  const [emailSended, setEmailSended] = useState('');
  const adminStore = useAdminStore.getState();

  useEffect(() => {
    const loginPaths = ['setpassword', 'resetpassword'];

    const pushToDashboard = () => {
      const { pathname } = location;
      if (
        (pathname === '/' || pathname === '/login') &&
        (!Boolean(systemId) || !Boolean(organizationId))
      )
        history.push('/dashboard');
    };

    if (loginPaths.includes(page)) {
      setAdminState(null);
      if (emailParam && token) {
        logOut();
      }
      if (page === 'resetpassword' && !emailParam && !token) {
        setResetPasswordState(true);
      } else if (!emailParam || !token) {
        history.replace('/login');
      }
    } else {
      const adminData = getAdminFromLocalS();
      if (adminData === null) {
        setAdminState(null);
        saveLoginRedirect(location.pathname, location.search);
        history.push('/login');
        return;
      }
      adminStore.setAdmin(adminData as IAdminResponse);
      adminLoaded(adminData as IAdmin);
      if (systemId && !organizationId) {
        // an explicit system link makes the system the active context again
        localStorage.removeItem('organizationId');
      }
      // like systemGid for systems, fall back to the last active organization
      const activeOrganizationId =
        organizationId || localStorage.getItem('organizationId');
      if (activeOrganizationId) {
        getOrganization(activeOrganizationId);
        pushToDashboard();
      } else {
        getAllSystems(adminData as IAdmin, systemId);
        pushToDashboard();
      }
    }
  }, [
    adminLoaded,
    emailParam,
    getAllSystems,
    page,
    token,
    location,
    systemId,
    organizationId,
    adminStore,
  ]);

  const login = (email: string, password: string) => {
    return newLogin(email, password).catch((e: { message: string }) => {
      if (e.message === 'Log in attempts exceeded') {
        dispatch('TO_MANY_LOGINS_RESET_PASSWORD', null);
        setResetPasswordState(true);
      } else {
        CustomToast({
          type: 'error',
          message: 'Bad email / password combination.',
        });
        setPassword('');
      }
    });
  };

  const handleResetPasswordCancel = () => {
    dispatch('CANCEL_RESET_PASSWORD', null);
    history.push('/login');
  };

  if (adminState === null) {
    if (resetPasswordState && !showResetSuccess) {
      return (
        <ResetPasswordWithTheme
          onSubmit={(email: string) => {
            setShowResetSuccess(true);
            setEmailSended(email);
          }}
          maxedOut={admin.maxedOut}
          cancel={handleResetPasswordCancel}
          initialEmail={emailSended}
        />
      );
    }
    if ((password && !showResetSuccess) || (emailParam && token && !showResetSuccess)) {
      return <SetPasswordReduxWithTheme />;
    }
    if (showResetSuccess) {
      return (
        <SuccessWithTheme
          email={emailSended}
          onChangeEmail={() => setShowResetSuccess(false)}
        />
      );
    }
    return <LoginWithTheme login={login} />;
  }
  return null;
};

export default withRouter(
  connect(
    (state: { admin: IAdmin }) => ({
      admin: state.admin,
    }),
    (dispatch) => ({
      // @ts-ignore
      newLogin: (email: string, password: string) => dispatch(newLogin(email, password)),
      adminLoaded: (admin: IAdmin) => {
        dispatch(adminLoaded(admin));
      },
      dispatch: (type: string, data: any) => {
        dispatch({ type, data });
      },
      getAllSystems: (adminData: any, systemId: string | null) => {
        // @ts-ignore
        dispatch(getAllSystems(adminData, systemId));
      },
    })
  )(LoginPage)
);
